/**
 * Publication-wide font substitution — replace every use of a family (object props, table cells, rich-text runs, inline
 * `font-family` in HTML, text on shapes, WordArt, groups, master pages) with another family.
 */

const clean = (family) => String(family || '').split(',')[0].replace(/["']/g, '').trim().toLowerCase();

/** Replacement for an object-level fontFamily value (plain family name — the renderer adds quotes and the generic fallback). */
const swapCss = (value, map) => {
  const to = map.get(clean(value));
  return to || String(value || '');
};

/** Inline `font-family: X` / `font-family: 'X'` / `font-family: "X", serif` inside HTML. */
const HTML_FONT_RE = /font-family:\s*(&quot;|['"])?([^;'">&]+)\1([^;">]*)/gi;

const swapHtml = (html, map, stats) => html.replace(HTML_FONT_RE, (m, q, fam, rest) => {
  const to = map.get(clean(fam));
  if (!to) return m;
  stats.count += 1;
  const quote = q || "'"; // keep the original quoting (style attributes are double-quoted, so bare names get single quotes)
  // A bare name may carry its generic fallback in the same capture ("Tw Cen MT, sans-serif") — keep the fallback.
  const tail = !q && fam.includes(',') ? fam.slice(fam.indexOf(',')) : '';
  return `font-family: ${quote}${to}${quote}${tail}${rest || ''}`;
});

const swapProps = (p, map, stats) => {
  if (!p || typeof p !== 'object') return p;
  let out = p, changed = false;
  const set = (k, v) => { if (!changed) { out = { ...p }; changed = true; } out[k] = v; };
  if (p.fontFamily && map.has(clean(p.fontFamily))) { set('fontFamily', swapCss(p.fontFamily, map)); stats.count += 1; }
  if (Array.isArray(p.cells)) {
    let cellsChanged = false;
    const cells = p.cells.map((row) => (Array.isArray(row) ? row.map((c) => {
      if (c && c.fontFamily && map.has(clean(c.fontFamily))) { cellsChanged = true; stats.count += 1; return { ...c, fontFamily: swapCss(c.fontFamily, map) }; }
      return c;
    }) : row));
    if (cellsChanged) set('cells', cells);
  }
  if (Array.isArray(p.runs)) {
    let runsChanged = false;
    const runs = p.runs.map((r) => {
      const fam = r?.style?.fontFamily || r?.fontFamily;
      if (fam && map.has(clean(fam))) {
        runsChanged = true; stats.count += 1;
        return r.style ? { ...r, style: { ...r.style, fontFamily: swapCss(fam, map) } } : { ...r, fontFamily: swapCss(fam, map) };
      }
      return r;
    });
    if (runsChanged) set('runs', runs);
  }
  for (const key of ['html', 'content']) {
    if (typeof p[key] === 'string' && /font-family/i.test(p[key])) {
      const before = stats.count;
      const next = swapHtml(p[key], map, stats);
      if (stats.count !== before) set(key, next);
    }
  }
  return out;
};

const swapObject = (o, map, stats) => {
  if (!o) return o;
  const props = swapProps(o.properties, map, stats);
  let children = o.children;
  if (Array.isArray(o.children)) {
    const next = o.children.map((c) => swapObject(c, map, stats));
    if (next.some((c, i) => c !== o.children[i])) children = next;
  }
  if (props === o.properties && children === o.children) return o;
  return { ...o, properties: props, children };
};

/**
 * Apply `mapping` ({ fromFamily: toFamily }) to pages + masters. Returns `{ pages, masters, count }` where `count` is the number
 * of replacements; pages / masters are the same references when nothing changed.
 */
export const swapFonts = (pages = [], masters = {}, mapping = {}) => {
  const map = new Map(Object.entries(mapping).filter(([from, to]) => from && to && clean(from) !== clean(to)).map(([from, to]) => [clean(from), String(to).split(',')[0].replace(/["']/g, '').trim()]));
  const stats = { count: 0 };
  if (!map.size) return { pages, masters, count: 0 };
  let pagesChanged = false;
  const nextPages = pages.map((pg) => {
    const objs = (pg.objects || []).map((o) => swapObject(o, map, stats));
    if (objs.some((o, i) => o !== pg.objects[i])) { pagesChanged = true; return { ...pg, objects: objs }; }
    return pg;
  });
  let mastersChanged = false;
  const nextMasters = {};
  Object.entries(masters || {}).forEach(([id, m]) => {
    const objs = (m.objects || []).map((o) => swapObject(o, map, stats));
    if (objs.some((o, i) => o !== m.objects[i])) { mastersChanged = true; nextMasters[id] = { ...m, objects: objs }; } else nextMasters[id] = m;
  });
  return { pages: pagesChanged ? nextPages : pages, masters: mastersChanged ? nextMasters : masters, count: stats.count };
};

/** How many places each family is used (object props, cells, runs, inline HTML). */
export const fontUsage = (pages = [], masters = {}) => {
  const usage = new Map();
  const bump = (fam) => { const k = String(fam || '').split(',')[0].replace(/["']/g, '').trim(); if (k) usage.set(k, (usage.get(k) || 0) + 1); };
  const visit = (o) => {
    if (!o) return;
    const p = o.properties || {};
    if (p.fontFamily) bump(p.fontFamily);
    if (Array.isArray(p.cells)) p.cells.forEach((row) => (Array.isArray(row) ? row : []).forEach((c) => c?.fontFamily && bump(c.fontFamily)));
    if (Array.isArray(p.runs)) p.runs.forEach((r) => { const f = r?.style?.fontFamily || r?.fontFamily; if (f) bump(f); });
    for (const key of ['html', 'content']) {
      if (typeof p[key] === 'string') { const re = new RegExp(HTML_FONT_RE.source, 'gi'); let m; while ((m = re.exec(p[key]))) bump(m[2]); }
    }
    (o.children || []).forEach(visit);
  };
  pages.forEach((pg) => (pg.objects || []).forEach(visit));
  Object.values(masters || {}).forEach((m) => (m.objects || []).forEach(visit));
  return usage;
};

/** A sensible default replacement for a family that is not available, guessed from its name. */
export const suggestReplacement = (family) => {
  const f = String(family || '').toLowerCase();
  if (/script|brush|hand|cursive|calligraph|vivaldi|mistral|lucida handwriting|segoe print|freestyle|edwardian|kunstler|french script|palace|monotype corsiva/.test(f)) return 'Dancing Script';
  if (/mono|console|courier|consolas|typewriter/.test(f)) return 'Roboto Mono';
  if (/impact|haettenschweiler|bernard|cooper|broadway|showcard|stencil|algerian|jokerman|chiller|ravie|snap|wide latin/.test(f)) return 'Bebas Neue';
  if (/condensed|narrow|compressed|tw cen|century gothic/.test(f)) return 'Oswald';
  if (/garamond|goudy|baskerville|caslon|book antiqua|palatino|perpetua|bookman|century schoolbook|georgia|cambria|times|minion|bodoni|didot|high tower|constantia|serif/.test(f)) return 'EB Garamond';
  if (/franklin|gill|futura|avenir|helvetica|arial|verdana|tahoma|segoe|calibri|candara|corbel|trebuchet|lucida sans|berlin sans|gothic|sans/.test(f)) return 'Open Sans';
  return 'Inter';
};
